// src/context/AlertsContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { CountyContext } from "../context/CountyContext.js";

const AlertsContext = createContext();

const EMPTY_FLAGS = {
  hasBurnBan: false,
  hasWinterStorm: false,
  hasWinterWatch: false,
  hasWinterWarning: false,
  hasSevereStorm: false,
  hasTornado: false,
  hasFlooding: false,
  hasFog: false,
  hasFireWeather: false,
  hasHighWinds: false,
  hasExtremeCold: false,
  hasExtremeHeat: false,
  hasSurfHazard: false,
};

const ALERT_DEBUG_MODE = false;

// Pick one or more scenario keys from MOCK_ALERT_SCENARIOS below.
const ALERT_DEBUG_SCENARIOS = [
  // "fireWeatherWatch",
  // "redFlagWarning",
  // "burnBanOnly",
  // "fireWeatherWatchPlusBurnBan",
  // "floodWarning",
  // "severeThunderstormWatch",
  // "severeThunderstormWarning",
  // "tornadoWarning",
  // "winterStormWatch",
  // "winterStormWarning",
  // "denseFogAdvisory",
  // "highWindWarning",
  // "heatAdvisory",
  // "extremeColdWarning",
  // "ripCurrentStatement",
  // "specialWeatherStatementThunderstorms",
  // "specialWeatherStatementHeat",
  // "specialWeatherStatementFireDanger",
];

const makeMockWeatherAlert = ({
  id,
  event,
  headline,
  description,
  instruction = "",
  severity = "warning",
}) => ({
  id,
  type: "weather",
  title: headline || event || "Weather alert",
  severity,
  source: "mock-nws",
  raw: {
    id,
    properties: {
      event,
      headline: headline || event,
      description: description || "",
      instruction,
    },
  },
});

const makeMockBurnBanAlert = ({
  id = "mock-burn-ban",
  title = "Burn ban in effect",
}) => ({
  id,
  type: "burnBan",
  title,
  severity: "warning",
  source: "mock-tfs",
  raw: {
    properties: {
      event: "Burn Ban",
      headline: title,
      description: "Outdoor burning is prohibited until further notice.",
    },
  },
});

const MOCK_ALERT_SCENARIOS = {
  fireWeatherWatch: [
    makeMockWeatherAlert({
      id: "mock-fire-weather-watch-1",
      event: "Fire Weather Watch",
      headline: "Fire Weather Watch issued May 13 at 2:00 PM CDT",
      description:
        "Dry fuels and gusty southwest winds may create critical fire weather conditions.",
    }),
  ],

  redFlagWarning: [
    makeMockWeatherAlert({
      id: "mock-red-flag-warning-1",
      event: "Red Flag Warning",
      headline: "Red Flag Warning remains in effect",
      description:
        "Critical fire weather conditions are expected due to low humidity and strong winds.",
    }),
  ],

  burnBanOnly: [
    makeMockBurnBanAlert({
      id: "mock-burn-ban-only-1",
      title: "Burn ban in effect",
    }),
  ],

  fireWeatherWatchPlusBurnBan: [
    makeMockWeatherAlert({
      id: "mock-fire-weather-watch-2",
      event: "Fire Weather Watch",
      headline: "Fire Weather Watch issued May 13 at 2:00 PM CDT",
      description:
        "Dry fuels and gusty winds may create critical fire weather conditions.",
    }),
    makeMockBurnBanAlert({
      id: "mock-burn-ban-2",
      title: "Burn ban in effect",
    }),
  ],

  floodWarning: [
    makeMockWeatherAlert({
      id: "mock-flood-warning-1",
      event: "Flood Warning",
      headline: "Flood Warning remains in effect",
      description:
        "Flooding is ongoing in low-lying areas and along creeks and roadways.",
    }),
  ],

  severeThunderstormWatch: [
    makeMockWeatherAlert({
      id: "mock-severe-thunderstorm-watch-1",
      event: "Severe Thunderstorm Watch",
      headline: "Severe Thunderstorm Watch issued for the area",
      description:
        "Conditions are favorable for severe thunderstorms with large hail and damaging winds.",
    }),
  ],

  severeThunderstormWarning: [
    makeMockWeatherAlert({
      id: "mock-severe-thunderstorm-warning-1",
      event: "Severe Thunderstorm Warning",
      headline: "Severe Thunderstorm Warning in effect",
      description:
        "Radar indicated a severe thunderstorm capable of producing damaging winds and hail.",
    }),
  ],

  tornadoWarning: [
    makeMockWeatherAlert({
      id: "mock-tornado-warning-1",
      event: "Tornado Warning",
      headline: "Tornado Warning issued for the area",
      description:
        "A severe thunderstorm capable of producing a tornado was located nearby.",
    }),
  ],

  winterStormWatch: [
    makeMockWeatherAlert({
      id: "mock-winter-storm-watch-1",
      event: "Winter Storm Watch",
      headline: "Winter Storm Watch issued",
      description:
        "Heavy snow, sleet, and freezing rain are possible later today.",
    }),
  ],

  winterStormWarning: [
    makeMockWeatherAlert({
      id: "mock-winter-storm-warning-1",
      event: "Winter Storm Warning",
      headline: "Winter Storm Warning remains in effect",
      description:
        "Heavy snow and ice accumulation are expected, making travel dangerous.",
    }),
  ],

  denseFogAdvisory: [
    makeMockWeatherAlert({
      id: "mock-dense-fog-advisory-1",
      event: "Dense Fog Advisory",
      headline: "Dense Fog Advisory in effect",
      description:
        "Visibility may drop below one quarter mile in dense fog overnight.",
    }),
  ],

  highWindWarning: [
    makeMockWeatherAlert({
      id: "mock-high-wind-warning-1",
      event: "High Wind Warning",
      headline: "High Wind Warning issued",
      description:
        "Sustained winds and gusts up to 60 mph may make travel difficult for high profile vehicles.",
    }),
  ],

  heatAdvisory: [
    makeMockWeatherAlert({
      id: "mock-heat-advisory-1",
      event: "Heat Advisory",
      headline: "Heat Advisory in effect",
      description:
        "Heat index values may reach 108 degrees. Hot conditions could cause heat illness.",
    }),
  ],

  extremeColdWarning: [
    makeMockWeatherAlert({
      id: "mock-extreme-cold-warning-1",
      event: "Extreme Cold Warning",
      headline: "Extreme Cold Warning in effect",
      description:
        "Dangerously cold wind chills may cause frostbite in minutes.",
    }),
  ],

  ripCurrentStatement: [
    makeMockWeatherAlert({
      id: "mock-rip-current-1",
      event: "Beach Hazards Statement",
      headline: "Beach Hazards Statement issued",
      description:
        "Strong rip currents and dangerous surf conditions are expected.",
    }),
  ],

  specialWeatherStatementThunderstorms: [
    makeMockWeatherAlert({
      id: "mock-sps-thunderstorms-1",
      event: "Special Weather Statement",
      headline: "Special Weather Statement",
      description:
        "A line of strong thunderstorms may produce gusty winds, lightning, and pea-size hail.",
    }),
  ],

  specialWeatherStatementHeat: [
    makeMockWeatherAlert({
      id: "mock-sps-heat-1",
      event: "Special Weather Statement",
      headline: "Special Weather Statement",
      description:
        "Heat index values may rise above 105 degrees this afternoon. Hot conditions may cause heat exhaustion.",
    }),
  ],

  specialWeatherStatementFireDanger: [
    makeMockWeatherAlert({
      id: "mock-sps-fire-danger-1",
      event: "Special Weather Statement",
      headline: "Special Weather Statement",
      description:
        "Elevated fire danger is expected due to dry grasses, low humidity, and gusty winds.",
    }),
  ],
};

// Higher number = show earlier within the same tier (used in NwsAlerts)
const getSubtypeSeverityRank = (feature) => {
  const props = feature && feature.properties ? feature.properties : {};
  const rawEvent = (props.event || "").toLowerCase();
  const rawHeadline = (props.headline || "").toLowerCase();
  const txt = rawEvent + " " + rawHeadline;

  const isWinterStorm =
    txt.indexOf("winter storm") !== -1 ||
    txt.indexOf("winter weather") !== -1 ||
    txt.indexOf("blizzard") !== -1 ||
    txt.indexOf("snow squall") !== -1;

  const isExtremeCold =
    txt.indexOf("extreme cold") !== -1 ||
    txt.indexOf("wind chill") !== -1 ||
    txt.indexOf("cold weather advisory") !== -1;

  if (isWinterStorm) return 2;
  if (isExtremeCold) return 1;
  return 0;
};

const safeLower = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeHazardId = (value) =>
  safeLower(value).replace(/_/g, "-").replace(/\s+/g, "-");

const normalizeResolvedAlertType = (value) => {
  const t = safeLower(value);

  if (!t) return null;
  if (t === "burn ban") return "Burn Ban";
  if (t === "red flag warning") return "Warning";
  if (t === "fire weather watch") return "Watch";
  if (t === "warning") return "Warning";
  if (t === "watch") return "Watch";
  if (t === "evacuation") return "Evacuation";
  if (t === "seasonal") return "Seasonal";

  return value;
};

const isGenericStatementLikeEvent = (event, headline) => {
  const e = safeLower(event);
  const h = safeLower(headline);

  return (
    e.includes("special weather statement") ||
    h.includes("special weather statement") ||
    e.includes("advisory") ||
    h.includes("advisory") ||
    e.includes("statement") ||
    h.includes("statement")
  );
};

const resolveAlertContext = (alert) => {
  if (!alert) return null;

  if (alert.type === "burnBan") {
    return {
      hazardId: "wildfire",
      hazardLabel: "Wildfire",
      alertType: "Burn Ban",
      checklistEligible: true,
    };
  }

  if (alert.type !== "weather") return null;

  const props = alert.raw?.properties || {};
  const rawEvent = props.event || "";
  const rawHeadline = props.headline || "";
  const rawDescription = props.description || "";

  const event = safeLower(rawEvent);
  const headline = safeLower(rawHeadline);
  const description = safeLower(rawDescription);
  const titleText = `${event} ${headline}`.trim();
  const genericProduct = isGenericStatementLikeEvent(rawEvent, rawHeadline);

  const titleHas = (needle) => titleText.includes(needle);
  const descHas = (needle) => description.includes(needle);
  const genericHas = (needle) => genericProduct && description.includes(needle);

  let hazardId = null;
  let hazardLabel = null;
  let alertType = null;
  let checklistEligible = true;

  // Alert name/title should trump description whenever available.
  if (
    titleHas("red flag warning") ||
    titleHas("fire weather watch") ||
    titleHas("fire weather") ||
    titleHas("red flag")
  ) {
    hazardId = "wildfire";
    hazardLabel = "Wildfire";

    if (titleHas("red flag warning")) {
      alertType = "Warning";
    } else if (titleHas("fire weather watch")) {
      alertType = "Watch";
    } else if (titleHas("warning")) {
      alertType = "Warning";
    } else if (titleHas("watch") || titleHas("advisory")) {
      alertType = "Watch";
    }
  } else if (
    titleHas("winter storm warning") ||
    titleHas("blizzard warning") ||
    titleHas("ice storm warning") ||
    titleHas("snow squall warning") ||
    titleHas("winter storm watch") ||
    titleHas("winter weather advisory") ||
    titleHas("blizzard watch") ||
    titleHas("freezing rain advisory") ||
    titleHas("freezing drizzle advisory")
  ) {
    hazardId = "winter-weather";
    hazardLabel = "Winter Weather";

    if (
      titleHas("warning") ||
      titleHas("blizzard warning") ||
      titleHas("ice storm warning") ||
      titleHas("snow squall warning")
    ) {
      alertType = "Warning";
    } else {
      alertType = "Watch";
    }
  } else if (
    titleHas("tornado warning") ||
    titleHas("tornado watch")
  ) {
    hazardId = "tornado";
    hazardLabel = "Tornado";
    alertType = titleHas("warning") ? "Warning" : "Watch";
  } else if (
    titleHas("severe thunderstorm warning") ||
    titleHas("severe thunderstorm watch")
  ) {
    hazardId = "severe-storms";
    hazardLabel = "Severe Storms";
    alertType = titleHas("warning") ? "Warning" : "Watch";
  } else if (titleHas("flood")) {
    hazardId = "flooding";
    hazardLabel = "Flooding";
    alertType = titleHas("warning") ? "Warning" : titleHas("watch") ? "Watch" : "Warning";
  } else if (
    titleHas("dense fog") ||
    titleHas("fog advisory")
  ) {
    hazardId = "dense-fog";
    hazardLabel = "Dense Fog";
    alertType = titleHas("warning") ? "Warning" : "Watch";
  } else if (
    titleHas("high wind") ||
    titleHas("wind advisory")
  ) {
    hazardId = "high-winds";
    hazardLabel = "High Winds";
    alertType = titleHas("warning") ? "Warning" : "Watch";
  } else if (
    titleHas("heat advisory") ||
    titleHas("excessive heat") ||
    titleHas("extreme heat")
  ) {
    hazardId = "extreme-heat";
    hazardLabel = "Extreme Heat";
    alertType = titleHas("warning") ? "Warning" : "Watch";
  } else if (
    titleHas("freeze") ||
    titleHas("frost") ||
    titleHas("wind chill") ||
    titleHas("extreme cold") ||
    titleHas("cold weather advisory")
  ) {
    hazardId = "extreme-cold";
    hazardLabel = "Extreme Cold";
    alertType = titleHas("warning") ? "Warning" : "Watch";
  } else if (
    titleHas("high surf") ||
    titleHas("rip current") ||
    titleHas("beach hazards")
  ) {
    hazardId = "surf-rip";
    hazardLabel = "Surf & Rip Currents";
    alertType = titleHas("warning") ? "Warning" : "Watch";
  }

  // Description parsing only for generic products.
  if (!hazardId && genericProduct) {
    if (
      genericHas("thunderstorm") ||
      genericHas("thunderstorms") ||
      genericHas("line of strong thunderstorms") ||
      genericHas("pea size hail") ||
      genericHas("pea-size hail") ||
      genericHas("lightning") ||
      genericHas("hail") ||
      genericHas("radar was tracking a line of strong thunderstorms")
    ) {
      hazardId = "severe-storms";
      hazardLabel = "Severe Storms";
      alertType = "Warning";
    } else if (
      genericHas("heat advisory") ||
      genericHas("excessive heat") ||
      genericHas("extreme heat") ||
      genericHas("heat index") ||
      genericHas("feels like") ||
      genericHas("apparent temperature") ||
      genericHas("apparent temperatures") ||
      genericHas("heat exhaustion") ||
      genericHas("heat stroke") ||
      genericHas("dangerously hot")
    ) {
      hazardId = "extreme-heat";
      hazardLabel = "Extreme Heat";
      alertType = "Warning";
    } else if (
      genericHas("elevated fire danger") ||
      genericHas("critical fire weather") ||
      genericHas("wildfire")
    ) {
      hazardId = "wildfire";
      hazardLabel = "Wildfire";
      alertType = "Warning";
    } else if (
      genericHas("snow") ||
      genericHas("freezing rain") ||
      genericHas("freezing drizzle") ||
      genericHas("sleet")
    ) {
      hazardId = "winter-weather";
      hazardLabel = "Winter Weather";
      alertType = "Watch";
    } else if (
      genericHas("flash flooding") ||
      genericHas("flash flood") ||
      genericHas("heavy rain")
    ) {
      hazardId = "flooding";
      hazardLabel = "Flooding";
      alertType = "Warning";
    } else if (
      genericHas("dense fog") ||
      genericHas("fog")
    ) {
      hazardId = "dense-fog";
      hazardLabel = "Dense Fog";
      alertType = "Watch";
    } else if (
      genericHas("strong winds") ||
      genericHas("damaging winds") ||
      genericHas("gusts") ||
      genericHas("high profile vehicles") ||
      /gusts?\s+(up to\s+)?\d{2}\s*mph/.test(description)
    ) {
      hazardId = "high-winds";
      hazardLabel = "High Winds";
      alertType = "Warning";
    }
  }

  // Air quality and other unsupported items can resolve as non-checklist-eligible.
  if (!hazardId) {
    checklistEligible = false;
  }

  return {
    hazardId,
    hazardLabel,
    alertType: normalizeResolvedAlertType(alertType),
    checklistEligible,
  };
};

const buildResolvedWeatherAlerts = (alerts) => {
  return (alerts || [])
    .filter((alert) => alert.type === "weather")
    .map((alert) => ({
      ...alert,
      resolved: resolveAlertContext(alert),
    }));
};

const buildFlagsFromAlerts = (nextAlerts, resolvedWeatherAlerts) => {
  let nextFlags = { ...EMPTY_FLAGS };

  (nextAlerts || []).forEach((a) => {
    if (a.type === "burnBan") {
      nextFlags.hasBurnBan = true;
    }
  });

  (resolvedWeatherAlerts || []).forEach((alert) => {
    const resolved = alert?.resolved;
    if (!resolved?.hazardId) return;

    const hazardId = normalizeHazardId(resolved.hazardId);
    const alertType = safeLower(resolved.alertType || "");

    if (hazardId === "wildfire") {
      nextFlags.hasFireWeather = true;
    }
    if (hazardId === "winter-weather") {
      nextFlags.hasWinterStorm = true;
      if (alertType === "warning") nextFlags.hasWinterWarning = true;
      if (alertType === "watch") nextFlags.hasWinterWatch = true;
    }
    if (hazardId === "severe-storms") {
      nextFlags.hasSevereStorm = true;
    }
    if (hazardId === "tornado") {
      nextFlags.hasTornado = true;
    }
    if (hazardId === "flooding") {
      nextFlags.hasFlooding = true;
    }
    if (hazardId === "dense-fog") {
      nextFlags.hasFog = true;
    }
    if (hazardId === "high-winds") {
      nextFlags.hasHighWinds = true;
    }
    if (hazardId === "extreme-cold") {
      nextFlags.hasExtremeCold = true;
    }
    if (hazardId === "extreme-heat") {
      nextFlags.hasExtremeHeat = true;
    }
    if (hazardId === "surf-rip") {
      nextFlags.hasSurfHazard = true;
    }
  });

  return nextFlags;
};

const getMockAlertsForScenarioKeys = (scenarioKeys) => {
  return (scenarioKeys || []).flatMap((key) => MOCK_ALERT_SCENARIOS[key] || []);
};

export const AlertsProvider = ({ children, countyMapping }) => {
  const { selectedCounty } = useContext(CountyContext);

  const [alerts, setAlerts] = useState([]);
  const [wildfireAlerts, setWildfireAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [flags, setFlags] = useState(EMPTY_FLAGS);
  const [flagsReady, setFlagsReady] = useState(false);

  const [useDebugAlerts, setUseDebugAlerts] = useState(ALERT_DEBUG_MODE);
  const [debugScenarioKeys, setDebugScenarioKeys] = useState(ALERT_DEBUG_SCENARIOS);
  const [debugCustomAlerts, setDebugCustomAlerts] = useState([]);

  const countyRef = useRef(selectedCounty);
  useEffect(() => {
    countyRef.current = selectedCounty;
  }, [selectedCounty]);

  const resetFlags = useCallback(() => {
    setFlags(EMPTY_FLAGS);
    setFlagsReady(false);
  }, []);

  const applyAlerts = useCallback(
    (nextAlerts) => {
      const resolved = buildResolvedWeatherAlerts(nextAlerts);
      const nextFlags = buildFlagsFromAlerts(nextAlerts, resolved);

      setAlerts(nextAlerts);
      setFlags(nextFlags);
      setFlagsReady(true);

      console.groupCollapsed("[AlertsContext] applied alerts");
      console.log("Alerts:", nextAlerts);
      console.log("Resolved weather alerts:", resolved);
      console.log("Flags:", nextFlags);
      console.groupEnd();
    },
    []
  );

  const debugScenarioAlerts = useMemo(
    () => getMockAlertsForScenarioKeys(debugScenarioKeys),
    [debugScenarioKeys]
  );

  const effectiveDebugAlerts = useMemo(() => {
    const fromWildfire = Array.isArray(wildfireAlerts) ? wildfireAlerts : [];
    const fromCustom = Array.isArray(debugCustomAlerts) ? debugCustomAlerts : [];
    return [...debugScenarioAlerts, ...fromCustom, ...fromWildfire];
  }, [debugScenarioAlerts, debugCustomAlerts, wildfireAlerts]);

  const fetchAlerts = useCallback(async () => {
    const countyAtStart = selectedCounty;

    if (useDebugAlerts) {
      setIsLoading(false);
      applyAlerts(effectiveDebugAlerts);
      return;
    }

    if (!countyAtStart) {
      if (countyAtStart === countyRef.current) {
        setAlerts([]);
        resetFlags();
      }
      return;
    }

    setIsLoading(true);
    setFlagsReady(false);

    try {
      const mapping = countyMapping || [];
      const selectedCountyData = mapping.find((c) => c.county === countyAtStart);

      if (!selectedCountyData) {
        if (countyAtStart === countyRef.current) {
          setAlerts([]);
          resetFlags();
        }
        return;
      }

      const nwsRes = await fetch(
        "https://api.weather.gov/alerts/active?zone=" +
          selectedCountyData.nws_county_code
      );
      if (!nwsRes.ok) throw new Error("NWS network error");
      const nwsJson = await nwsRes.json();

      console.groupCollapsed("[AlertsContext] NWS raw alerts");
      console.log("Request URL:", nwsRes.url);
      console.log("Features:", nwsJson.features);
      console.groupEnd();

      const weatherAlerts = (nwsJson.features || []).map((feature) => ({
        id: feature.id,
        type: "weather",
        title:
          (feature.properties && feature.properties.headline) ||
          "Weather alert",
        severity: "warning",
        source: "nws",
        raw: feature,
        eventSubtypeSeverity: getSubtypeSeverityRank(feature),
      }));

      const burnRes = await fetch(
        "https://tfsgis.tfs.tamu.edu/arcgis/rest/services/BurnBan/BurnBan/MapServer/0/query?f=json&where=County%20%3D%20'" +
          countyAtStart +
          "'%20AND%20BurnBan%20%3D%20'Yes'&returnGeometry=false&outFields=County,BurnBan"
      );
      if (!burnRes.ok) throw new Error("Burn ban network error");
      const burnJson = await burnRes.json();
      const hasBurnBan = burnJson.features && burnJson.features.length > 0;

      const burnAlerts = hasBurnBan
        ? [
            {
              id: "burnban-" + countyAtStart,
              type: "burnBan",
              title: "Burn ban in effect",
              severity: "warning",
              source: "tfs",
              raw: burnJson,
            },
          ]
        : [];

      const nextAlerts = weatherAlerts.concat(burnAlerts).concat(wildfireAlerts);

      if (countyAtStart === countyRef.current) {
        applyAlerts(nextAlerts);
      }
    } catch (e) {
      if (countyAtStart === countyRef.current) {
        console.error("Error fetching alerts:", e);
        setAlerts([]);
        resetFlags();
      }
    } finally {
      if (countyAtStart === countyRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    selectedCounty,
    countyMapping,
    wildfireAlerts,
    useDebugAlerts,
    effectiveDebugAlerts,
    applyAlerts,
    resetFlags,
  ]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    if (useDebugAlerts) return;

    const id = setInterval(() => {
      fetchAlerts();
    }, 900000);

    return () => clearInterval(id);
  }, [fetchAlerts, useDebugAlerts]);

  const resolvedWeatherAlerts = useMemo(
    () => buildResolvedWeatherAlerts(alerts),
    [alerts]
  );

  const refreshAlerts = useCallback(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const clearDebugAlerts = useCallback(() => {
    setDebugScenarioKeys([]);
    setDebugCustomAlerts([]);
  }, []);

  return (
    <AlertsContext.Provider
      value={{
        alerts,
        resolvedWeatherAlerts,
        isLoading,
        refreshAlerts,
        flags,
        flagsReady,
        setWildfireAlerts,

        useDebugAlerts,
        setUseDebugAlerts,
        debugScenarioKeys,
        setDebugScenarioKeys,
        debugCustomAlerts,
        setDebugCustomAlerts,
        clearDebugAlerts,
        availableDebugScenarios: Object.keys(MOCK_ALERT_SCENARIOS),
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
};

export const useAlerts = () => {
  const ctx = useContext(AlertsContext);
  if (!ctx) {
    throw new Error("useAlerts must be used within AlertsProvider");
  }
  return ctx;
};